import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { TooltipProps } from 'components/Tooltip'
import { Alert } from 'features/alerts/types/alert'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useAbility } from 'providers/casl'
import { AdUnitAlerts } from '../../components/AdUnitAlerts'
import { KNOWLEDGE_BASE_URL } from '../../constants'
import { usePriceGeniusAlwaysOn } from '../../hooks/usePriceGeniusAlwaysOn'
import { AdUnitAlert } from '../../types/adUnitAlert'
import { CommonSchema } from './schemas/commonSchema'

export const CommonForm = (): JSX.Element => {
  const { t } = useTranslation(['features/adUnit', 'common'])
  const { adUnit } = useContext(AddEditAdUnitContext)
  const { control } = useFormContext<CommonSchema>()
  const { priceGeniusAlwaysOn } = usePriceGeniusAlwaysOn()
  const ability = useAbility()
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')

  if (!adUnit) {
    throw Error('Ad unit should be accessible')
  }

  const { alerts } = adUnit

  const tooltip: TooltipProps = {
    content: t('form.priceGenius.enabled.tooltip'),
    link: {
      title: t('common:learnMore'),
      url: KNOWLEDGE_BASE_URL,
    },
  }

  const priceGeniusAlerts: Alert<AdUnitAlert>[] = alerts.filter(
    (alert: Alert<AdUnitAlert>): boolean => alert.name.startsWith('priceGenius'),
  )

  return (
    <Paper>
      <SectionHeader
        subtitle={t('form.priceGenius.enabled.description')}
        title={t('form.priceGenius.enabled.title')}
        titleEndAdornment={
          <FlexBox axis='x'>
            <Switch
              control={control}
              disabled={cannotUpdateAdUnit || priceGeniusAlwaysOn}
              name='priceGeniusEnabled'
              {...{
                tooltip,
                tooltipMode: 'wrap',
              }}
            />

            {priceGeniusAlerts.length > 0 && <AdUnitAlerts alerts={priceGeniusAlerts} />}
          </FlexBox>
        }
      />
    </Paper>
  )
}
